'use client';

import type { ClassValue } from 'clsx';
import type { ChangeEvent, Dispatch, SetStateAction } from 'react';
import {
  AlphabetRail,
  AlphabetRailItem,
  AlphabetRailItemVariant,
  AlphabetRailLetter,
  AlphabetRailLetterState,
  AlphabetRailReset,
  AlphabetRailResetState,
} from '@vs-shop/commerce-ui/alphabetRail';
import { IndexSearchField, IndexSearchFieldClear } from '@vs-shop/commerce-ui/indexSearchField';
import { manipulateUrlSearchParams } from '@vs-shop/domain/string/urlSearchParams';
import { Input } from '@vs-shop/ui/input';
import { clsxm } from '@vs-shop/utils';
import { Option } from 'effect';
import { X } from 'lucide-react';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useDebounceCallback } from 'usehooks-ts';

import { alphabet } from '../../../../config/alphabet';
import { searchFilterDebounceMs } from '../../../../config/brands';

import BrandsForGivenFilters from './BrandsForGivenFilters';

import { getBrandsAlphabetAtom } from '../../../../effect/atom/brand/getBrandsAlphabet';
import {
  alphabetFilterSearchParamAtom,
  loadMorePagesAtom,
  pageSearchParamAtom,
  searchFilterSearchParamAtom,
} from '@vs-shop/engine/atom/brand/pagination';
import * as AsyncResult from 'effect/unstable/reactivity/AsyncResult';
import { useAtomSet, useAtomValue } from '@effect/atom-react';

type Props = {
  className?: ClassValue;
  canonicalUrl: string;
};

export type AlphabetOption = Option.Option<string>;

type BrandsAlphabetRailProps = {
  alphabetFilter: AlphabetOption;
  setAlphabetFilterAction: Dispatch<SetStateAction<AlphabetOption>>;
  className?: ClassValue;
  canonicalUrl: string;
};

/**
 * How this page's rail is sized.
 *
 * The shared rail states no box (see its own note): its two callers in this
 * repo are two shipped pages at two sizes, and neither may be resized by an
 * edit to the other. These are `/znacky`'s, unchanged — a 40px tile in a track
 * that may narrow to 32px, which is what fits thirty-six of them on one row of
 * a phone.
 */
const brandsAlphabetItemClasses = 'max-w-10 min-w-8';

const brandsAlphabetLetterClasses = 'h-10 cursor-pointer tab:min-h-auto tab:min-w-auto';

const brandsAlphabetResetClasses =
  'h-10 cursor-pointer tab:h-auto tab:w-auto tab:px-0 tab:text-[28px]/6 tab:ring-0';

/**
 * The brands index rail over `@vs-shop/commerce-ui/alphabetRail`.
 *
 * Which initials the catalogue reports and what pressing one does to this
 * page's filter stay here: the shared rail takes the selection as an appearance
 * and emits links, so the atom read and the toggle live at their one call site.
 *
 * Every letter is a link, including the ones the catalogue holds nothing under.
 * That is this page as it ships and this pass does not change it; the rail
 * offers `AlphabetRailLetterPlaceholder` for a page that would rather not, and
 * the glossary uses it.
 */
function BrandsAlphabetRail({
  alphabetFilter,
  setAlphabetFilterAction,
  className,
  canonicalUrl,
}: BrandsAlphabetRailProps) {
  const intl = useIntl();

  const alphabetResponse = useAtomValue(getBrandsAlphabetAtom);

  const lettersWithOccurrence = AsyncResult.match(alphabetResponse, {
    onInitial: () => [] as readonly string[],
    onFailure: () => [] as readonly string[],
    onSuccess: (success) => success.value.alphabet,
  });

  const activeLetter = Option.getOrNull(alphabetFilter);
  const allLabel = intl.formatMessage({
    id: 'common.all',
    defaultMessage: 'vše',
  });

  return (
    <AlphabetRail
      aria-label={intl.formatMessage({
        id: 'component.client.page.brands.brandsSearchAlphabet.alphabetLabel',
        defaultMessage: 'Abeceda',
      })}
      className={clsxm(className)}
    >
      {alphabet.map((letter) => {
        const hasLetterOccurrence = lettersWithOccurrence.includes(letter);
        const isActive = letter === activeLetter;

        return (
          <AlphabetRailItem
            key={letter}
            variant={AlphabetRailItemVariant.LETTER}
            className={brandsAlphabetItemClasses}
          >
            <AlphabetRailLetter
              href={manipulateUrlSearchParams(canonicalUrl, {
                alphabet: letter,
                page: null,
              })}
              data-letter={letter}
              state={
                isActive
                  ? AlphabetRailLetterState.SELECTED
                  : hasLetterOccurrence
                    ? AlphabetRailLetterState.AVAILABLE
                    : AlphabetRailLetterState.UNAVAILABLE
              }
              aria-current={isActive ? 'true' : undefined}
              aria-disabled={hasLetterOccurrence ? undefined : 'true'}
              className={brandsAlphabetLetterClasses}
              onClick={(e) => {
                e.preventDefault();
                // Toggle: if same letter is clicked, deselect it
                setAlphabetFilterAction((prev) =>
                  Option.match(prev, {
                    onNone: () => Option.some(letter),
                    onSome: (val) => (letter === val ? Option.none() : Option.some(letter)),
                  })
                );
              }}
            >
              {letter}
            </AlphabetRailLetter>
          </AlphabetRailItem>
        );
      })}

      <AlphabetRailItem variant={AlphabetRailItemVariant.RESET}>
        <AlphabetRailReset
          href={manipulateUrlSearchParams(canonicalUrl, {
            alphabet: null,
            page: null,
          })}
          state={
            activeLetter === null
              ? AlphabetRailResetState.SELECTED
              : AlphabetRailResetState.AVAILABLE
          }
          aria-current={activeLetter === null ? 'true' : undefined}
          aria-label={allLabel}
          className={brandsAlphabetResetClasses}
          onClick={(e) => {
            e.preventDefault();
            setAlphabetFilterAction(Option.none());
          }}
        >
          {allLabel}
        </AlphabetRailReset>
      </AlphabetRailItem>
    </AlphabetRail>
  );
}

export default function BrandsSearch({ className, canonicalUrl }: Props) {
  const intl = useIntl();

  // Read current filter values from URL
  const alphabetFilterOption = useAtomValue(alphabetFilterSearchParamAtom);
  const searchFilterOption = useAtomValue(searchFilterSearchParamAtom);

  // Set filter atoms (syncs to URL automatically)
  const setAlphabetFilterAtom = useAtomSet(alphabetFilterSearchParamAtom);
  const setSearchFilterAtom = useAtomSet(searchFilterSearchParamAtom);
  const setPageAtom = useAtomSet(pageSearchParamAtom);
  const setLoadMorePages = useAtomSet(loadMorePagesAtom);

  // Local state for input value (before debounce)
  const [searchValue, setSearchValue] = useState<string>(() =>
    Option.getOrElse(searchFilterOption, () => '')
  );

  const handleSearchDebouncedChange = useDebounceCallback((e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim();
    setSearchFilterAtom(value.length > 0 ? Option.some(value) : Option.none());
    // Reset page to 1 and load-more counter when search changes
    setPageAtom(Option.none());
    setLoadMorePages(0);
    // Clear alphabet filter when search is active (search across all brands)
    if (value.length > 0) {
      setAlphabetFilterAtom(Option.none());
    }
  }, searchFilterDebounceMs);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    handleSearchDebouncedChange(e);
  };

  const handleClear = () => {
    setSearchValue('');
    setSearchFilterAtom(Option.none());
  };

  const setAlphabetFilter: Dispatch<SetStateAction<AlphabetOption>> = (setState) => {
    setAlphabetFilterAtom(setState);
    // Reset page to 1 and load-more counter when alphabet filter changes
    setPageAtom(Option.none());
    setLoadMorePages(0);
  };

  return (
    <div data-slot="brands-search" className={clsxm('flex flex-col items-center', className)}>
      <div
        data-slot="brands-alphabet-prompt"
        className="mb-7 text-[22px] text-foreground tab:mb-16 tab:text-[28px]"
      >
        {intl.formatMessage({
          id: 'container.brands.abc.select',
          defaultMessage: 'Zvolte si z abecedy',
        })}
      </div>

      <BrandsAlphabetRail
        alphabetFilter={alphabetFilterOption}
        setAlphabetFilterAction={setAlphabetFilter}
        canonicalUrl={canonicalUrl}
        className="mb-8 tab:mb-28"
      />

      <IndexSearchField className="mb-8 tab:mb-16">
        <Input
          id="brands-search"
          value={searchValue}
          onChange={handleInputChange}
          label={intl.formatMessage({
            id: 'container.brands.abc.input.label',
            defaultMessage: 'Název nějaké značky',
          })}
        />
        {searchValue && (
          <IndexSearchFieldClear
            className="right-4 p-2"
            onClick={handleClear}
            aria-label={intl.formatMessage({
              id: 'container.brands.abc.input.clear',
              defaultMessage: 'Vyčistit vyhledávání',
            })}
          >
            <X className="w-4" />
          </IndexSearchFieldClear>
        )}
      </IndexSearchField>

      <BrandsForGivenFilters canonicalUrl={canonicalUrl} />
    </div>
  );
}
